import { assertEventRegistrationAllowed, type CreateEventInput, type EventListInput, type EventRepository, type RegisterEventPhotoInput, type TenantContext, type UpdateEventInput } from '@zetyar/data-contracts'
import type { CalendarEvent, EventPhoto, UUID } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

// Private bucket created in 0040_event_photos.sql.
const EVENT_PHOTOS_BUCKET = 'org-event-photos'
const PHOTO_URL_SECONDS = 600

interface EventPhotoRow {
  readonly id: UUID
  readonly organization_id: UUID
  readonly event_id: UUID
  readonly uploaded_by_membership_id: UUID
  readonly storage_path: string
  readonly thumb_path: string
  readonly byte_size: number
  readonly thumb_byte_size: number
  readonly width: number
  readonly height: number
  readonly caption: string | null
  readonly created_at: string
}

function mapEventPhoto(row: EventPhotoRow): EventPhoto {
  return {
    id: row.id,
    organizationId: row.organization_id,
    eventId: row.event_id,
    storagePath: row.storage_path,
    thumbPath: row.thumb_path,
    byteSize: row.byte_size,
    thumbByteSize: row.thumb_byte_size,
    width: row.width,
    height: row.height,
    caption: row.caption,
    uploadedByMembershipId: row.uploaded_by_membership_id,
    createdAt: row.created_at,
  }
}

const PHOTO_SELECT =
  'id, organization_id, event_id, uploaded_by_membership_id, storage_path, thumb_path, byte_size, thumb_byte_size, width, height, caption, created_at'

interface EventRow {
  readonly id: UUID
  readonly organization_id: UUID
  readonly title: string
  readonly description: string | null
  readonly location: string | null
  readonly is_virtual: boolean
  readonly virtual_url: string | null
  readonly start_date: string
  readonly end_date: string
  readonly capacity: number | null
  readonly status: string
  readonly created_at: string
  readonly updated_at: string
  readonly organizer: { readonly id: UUID } | null
  readonly event_registrations: readonly { readonly id: UUID }[] | null
}

function mapEvent(row: EventRow): CalendarEvent {
  return {
    id: row.id,
    organizationId: row.organization_id,
    title: row.title,
    description: row.description,
    location: row.location,
    isVirtual: row.is_virtual,
    virtualUrl: row.virtual_url,
    startDate: row.start_date,
    endDate: row.end_date,
    capacity: row.capacity,
    status: row.status,
    organizerId: row.organizer?.id ?? '',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: null,
    isRegistered: (row.event_registrations?.length ?? 0) > 0,
  }
}

const EVENT_SELECT = `
  id, organization_id, title, description, location, is_virtual, virtual_url,
  start_date, end_date, capacity, status, created_at, updated_at,
  organizer:memberships!events_organizer_membership_id_fkey (id)
`

export function createSupabaseEventRepository(client: SupabaseClient): EventRepository {
  return {
    async listVisible(context: TenantContext, { limit, fromDate }: EventListInput) {
      let query = client
        .from('events')
        .select(`${EVENT_SELECT}, event_registrations!left(id)`)
        .eq('organization_id', context.organizationId)
        .eq('event_registrations.membership_id', context.membershipId ?? '00000000-0000-0000-0000-000000000000')
        .eq('event_registrations.status', 'registered')
      if (fromDate) query = query.gte('end_date', fromDate)

      const { data, error } = await query
        .order('start_date', { ascending: true })
        .limit(Math.max(1, Math.min(limit, 100)))

      if (error) throw error
      return (data as unknown as EventRow[]).map(mapEvent)
    },

    async register(context: TenantContext, eventId: UUID) {
      if (!context.membershipId) throw new Error('Membership is required to register for an event')

      const { data: eventRow, error: eventError } = await client
        .from('events')
        .select('status, start_date, end_date, capacity')
        .eq('id', eventId)
        .single()
      if (eventError) throw eventError

      const { count: registeredCount } = await client
        .from('event_registrations')
        .select('id', { count: 'exact', head: true })
        .eq('event_id', eventId)
        .eq('membership_id', context.membershipId)
        .eq('status', 'registered')

      assertEventRegistrationAllowed({
        status: eventRow.status,
        startDate: eventRow.start_date,
        endDate: eventRow.end_date,
        capacity: eventRow.capacity,
        isRegistered: (registeredCount ?? 0) > 0,
      })

      const qrToken = `QR-${crypto.randomUUID()}`
      const { data, error } = await client
        .from('event_registrations')
        .insert({ event_id: eventId, membership_id: context.membershipId, qr_token: qrToken, status: 'registered' })
        .select('id, event_id, membership_id, status, qr_token, checked_in_at, created_at, updated_at')
        .single()

      if (error) throw error
      return {
        id: data.id as UUID,
        eventId: data.event_id as UUID,
        membershipId: data.membership_id as UUID,
        status: data.status as 'registered',
        qrToken: data.qr_token as string,
        checkedInAt: data.checked_in_at as string | null,
        createdAt: data.created_at as string,
        updatedAt: data.updated_at as string,
      }
    },

    async create(context: TenantContext, input: CreateEventInput) {
      const { data, error } = await client
        .rpc('create_event', {
          p_organization_id: context.organizationId,
          p_title: input.title,
          p_start_date: input.startDate,
          p_end_date: input.endDate,
          p_description: input.description ?? null,
          p_location: input.location ?? null,
          p_is_virtual: input.isVirtual ?? false,
          p_virtual_url: input.virtualUrl ?? null,
          p_capacity: input.capacity ?? null,
        })
        .single()

      if (error) throw error
      const row = data as Record<string, unknown>
      return mapEvent({
        id: row.id as UUID,
        organization_id: row.organization_id as UUID,
        title: row.title as string,
        description: (row.description as string | null) ?? null,
        location: (row.location as string | null) ?? null,
        is_virtual: row.is_virtual as boolean,
        virtual_url: (row.virtual_url as string | null) ?? null,
        start_date: row.start_date as string,
        end_date: row.end_date as string,
        capacity: (row.capacity as number | null) ?? null,
        status: row.status as string,
        created_at: row.created_at as string,
        updated_at: row.updated_at as string,
        organizer: { id: row.organizer_membership_id as UUID },
        event_registrations: [],
      })
    },

    async update(context: TenantContext, eventId: UUID, input: UpdateEventInput) {
      const { data, error } = await client
        .rpc('update_event', {
          p_id: eventId,
          p_organization_id: context.organizationId,
          p_title: input.title ?? null,
          p_description: input.description ?? null,
          p_location: input.location ?? null,
          p_is_virtual: input.isVirtual ?? null,
          p_virtual_url: input.virtualUrl ?? null,
          p_start_date: input.startDate ?? null,
          p_end_date: input.endDate ?? null,
          p_capacity: input.capacity ?? null,
        })
        .single()

      if (error) throw error
      const row = data as Record<string, unknown>
      return mapEvent({
        id: row.id as UUID,
        organization_id: row.organization_id as UUID,
        title: row.title as string,
        description: (row.description as string | null) ?? null,
        location: (row.location as string | null) ?? null,
        is_virtual: row.is_virtual as boolean,
        virtual_url: (row.virtual_url as string | null) ?? null,
        start_date: row.start_date as string,
        end_date: row.end_date as string,
        capacity: (row.capacity as number | null) ?? null,
        status: row.status as string,
        created_at: row.created_at as string,
        updated_at: row.updated_at as string,
        organizer: { id: row.organizer_membership_id as UUID },
        event_registrations: [],
      })
    },

    async delete(context: TenantContext, eventId: UUID) {
      const { error } = await client.rpc('delete_event', { p_id: eventId, p_organization_id: context.organizationId })
      if (error) throw error
    },

    async listPhotos(context: TenantContext, eventId: UUID) {
      const { data, error } = await client
        .from('event_photos')
        .select(PHOTO_SELECT)
        .eq('organization_id', context.organizationId)
        .eq('event_id', eventId)
        .order('created_at', { ascending: true })

      if (error) throw error
      return (data as unknown as EventPhotoRow[]).map(mapEventPhoto)
    },

    async registerPhoto(context: TenantContext, input: RegisterEventPhotoInput) {
      const { data, error } = await client
        .rpc('register_event_photo', {
          p_organization_id: context.organizationId,
          p_event_id: input.eventId,
          p_storage_path: input.storagePath,
          p_thumb_path: input.thumbPath,
          p_byte_size: input.byteSize,
          p_thumb_byte_size: input.thumbByteSize,
          p_width: input.width,
          p_height: input.height,
          p_caption: input.caption ?? null,
        })
        .single()

      if (error) throw error
      return mapEventPhoto(data as unknown as EventPhotoRow)
    },

    async deletePhoto(context: TenantContext, photoId: UUID) {
      const { error } = await client.rpc('delete_event_photo', {
        p_organization_id: context.organizationId,
        p_id: photoId,
      })
      if (error) throw error
    },

    // One request for the whole grid instead of a signed URL per thumbnail.
    async getPhotoUrls(_context: TenantContext, paths: readonly string[]) {
      if (paths.length === 0) return {}

      const { data, error } = await client.storage
        .from(EVENT_PHOTOS_BUCKET)
        .createSignedUrls([...paths], PHOTO_URL_SECONDS)
      if (error) throw error

      const urls: Record<string, string> = {}
      for (const item of data ?? []) {
        if (item.path && item.signedUrl) urls[item.path] = item.signedUrl
      }
      return urls
    },

    async getPhotoDownloadUrl(_context: TenantContext, path: string, fileName: string) {
      // `download` makes Storage answer with Content-Disposition: attachment. A plain <a download>
      // would be ignored because the signed URL is on another origin.
      const { data, error } = await client.storage
        .from(EVENT_PHOTOS_BUCKET)
        .createSignedUrl(path, 60, { download: fileName })
      if (error) throw error
      if (!data?.signedUrl) throw new Error('No se pudo generar el enlace de descarga.')
      return data.signedUrl
    },

    async recordAttendanceQR(context: TenantContext, registrationId: UUID) {
      const { error } = await client.rpc('record_attendance_qr', {
        p_registration_id: registrationId,
        p_organization_id: context.organizationId,
      })
      if (error) throw error
    },
  }
}
